import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import moment from 'moment'
import SalesDataTable from './SalesDataTable'
import { Typography, Snackbar, Alert, CircularProgress } from '@mui/material'

const SalesAdjustments = () => {
    const { isAdmin, isManagerFor, userInfo } = useContext(EmploymentsContext)
    const { calendarYearMonth, employmentId } = useParams()
    const [salePreds, setSalePreds] = useState({})
    const [mpPreds, setMpPreds] = useState({})
    const [customerPreds, setCustomerPreds] = useState({})
    const [storeId, setStoreId] = useState(null)
    const [monthlySalesPredTotal, setMonthlySalesPredTotal] = useState(0)
    const [monthlyMpPredsTotal, setMonthlyMpPredsTotal] = useState(0)
    const [monthlyCustomerPredsTotal, setMonthlyCustomerPredsTotal] = useState(0)
    const [monthlyPrevYearSalesTotal, setMonthlyPrevYearSalesTotal] = useState(0)
    const [prevYearSales, setPrevYearSales] = useState({})
    const [memos, setMemos] = useState({})
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState('success')
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (!employmentId) {
            setIsLoading(false)
            return
        }

        AxiosWithAuth.get('/employments')
            .then(res => {
                const employment = res.data.find(employment => parseInt(employment.id) === parseInt(employmentId))
                if (employment) {
                    setStoreId(employment.store_id)
                } else {
                    console.error('Employment not found:', employmentId)
                    setIsLoading(false)
                }
            })
            .catch(error => {
                console.error('Error fetching employments:', error)
                setIsLoading(false)
            })
    }, [employmentId])

    useEffect(() => {
        const fetchData = async () => {
            // Keep the spinner up while route params / storeId are still resolving.
            // Clearing isLoading here used to mount the table with empty maps, which
            // formatCurrency renders as "0" for every day until the real fetch finishes.
            if (!employmentId || !storeId || !calendarYearMonth) {
                return
            }

            const hasPermission = isAdmin || isManagerFor[employmentId] || userInfo?.view_only
            const isContextLoaded = userInfo && Object.keys(userInfo).length > 0
            const hasExplicitPermission = isAdmin || isManagerFor[employmentId]

            if (!isContextLoaded && !hasExplicitPermission) {
                return
            }

            if (!hasPermission) {
                setIsLoading(false)
                return
            }

            setIsLoading(true)
            try {
                // Normalize calendarYearMonth to YYYY-MM format to prevent double appending
                const normalizedYearMonth = moment(calendarYearMonth, ['YYYY-MM', 'YYYY-MM-DD'], true).format('YYYY-MM')
                const dateParam = normalizedYearMonth + '-01'

                let predsTotal = {}
                let predsCount = 0
                let staffRequirements = await AxiosWithAuth.get(`/stores/${storeId}/staff_requirements`, {
                    params: { month: normalizedYearMonth }
                })
                const prevYear = moment(normalizedYearMonth, 'YYYY-MM').subtract(1, 'month').format('YYYY-MM')
                let prevYearSalesCount = 0
                let prevYearSales = {}
                staffRequirements.data.forEach(req => {
                    if (moment(req.day).format('YYYY-MM') === normalizedYearMonth) {
                        if (predsTotal[req.day]) {
                            predsTotal[req.day] += req.pred_sales
                        } else {
                            predsTotal[req.day] = req.pred_sales
                        }
                    }
                    if (moment(req.day).format('YYYY-MM') === prevYear) {
                        if (prevYearSales[req.day]) {
                            prevYearSales[req.day] += req.pred_sales
                        } else {
                            prevYearSales[req.day] = req.pred_sales
                        }
                    }
                })
                Object.entries(predsTotal).forEach(total => {
                    predsTotal[total[0]] = Math.round(total[1])
                    predsCount += total[1]
                })
                Object.entries(prevYearSales).forEach(total => {
                    prevYearSales[total[0]] = Math.round(total[1])
                    prevYearSalesCount += total[1]
                })
                setMonthlyPrevYearSalesTotal(prevYearSalesCount)
                setPrevYearSales(prevYearSales)
                setSalePreds(predsTotal)
                setMonthlySalesPredTotal(predsCount)
                let customerPredsTotal = { ...predsTotal }
                let mpPredsTotal = {}
                let ids = {}
                let customerPredsCount = 0
                let mpPredsCount = 0
                let customerPredictions = await AxiosWithAuth.get('/customer_predictions', {
                    params: { store_id: storeId, date: dateParam }
                })
                setMemos(customerPredictions.data.reduce((acc, curr) => {
                    acc[curr.date] = curr.memo
                    return acc
                }, {}))
                customerPredictions.data.forEach(total => {
                    ids[total['date']] = total['id']
                    if (total['prediction'] !== null) customerPredsTotal[total['date']] = total['prediction']
                    if (total['mp_prediction'] != null) {
                        mpPredsTotal[total['date']] = total['mp_prediction']
                        mpPredsCount += total['mp_prediction']
                    }
                })
                Object.values(customerPredsTotal).forEach(key => {
                    customerPredsCount += key
                })
                setMpPreds(mpPredsTotal)
                setMonthlyMpPredsTotal(mpPredsCount)
                setCustomerPreds({ ...customerPredsTotal, ids })
                setMonthlyCustomerPredsTotal(customerPredsCount)
            } catch (error) {
                console.error('Error fetching sales adjustments data:', error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [storeId, calendarYearMonth, employmentId, isAdmin, isManagerFor, userInfo])

    const handleCustomerPredsChange = (date, value) => {
        setCustomerPreds(prev => ({
            ...prev,
            [date]: value
        }))
    }

    const handleSaveCustomerPreds = async (date, key, val) => {
        try {
            let newCustomerPreds = { ...customerPreds }
            let newVal = val
            if (key === 'prediction') {
                // Handle comma-separated numbers
                newVal = parseInt(val.toString().replace(/,/g, ''), 10) || 0
            } else if (key === 'memo') {
                newVal = val.trim()
            }

            if (Object.keys(customerPreds.ids).includes(date)) {
                let predId = customerPreds.ids[date]
                const res = await AxiosWithAuth.put(`/customer_predictions/${predId}`, {
                    [key]: newVal
                })
                newCustomerPreds = { ...newCustomerPreds, [res.data.date]: parseInt(res.data.prediction) }
                setCustomerPreds(newCustomerPreds)
            } else {
                const res = await AxiosWithAuth.post('/customer_predictions', {
                    store_id: storeId,
                    date: date,
                    [key]: newVal
                })
                newCustomerPreds = {
                    ...newCustomerPreds,
                    [res.data.date]: parseInt(res.data.prediction),
                    ids: { ...newCustomerPreds.ids, [res.data.date]: res.data.id }
                }
                setCustomerPreds(newCustomerPreds)
            }

            const message = key === 'prediction' ? '予定売上を保存しました' : 'メモを保存しました'
            setSnackbarMessage(message)
            setSnackbarSeverity('success')
            setSnackbarOpen(true)
        } catch (error) {
            console.error('Error saving customer prediction:', error)
            setSnackbarMessage('保存に失敗しました')
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
        }
    }

    const handleBulkSaveCustomerPreds = async (predictionsData) => {
        try {
            const predictions = predictionsData.map(({ date, prediction }) => ({
                date,
                prediction: parseInt(prediction.toString().replace(/,/g, ''), 10) || 0
            }))

            const res = await AxiosWithAuth.put('/customer_predictions/bulk', {
                store_id: storeId,
                predictions
            })

            // Update local state with the results
            let newCustomerPreds = { ...customerPreds }
            res.data.results.forEach(result => {
                newCustomerPreds[result.date] = result.prediction
                newCustomerPreds.ids = { ...newCustomerPreds.ids, [result.date]: result.id }
            })
            setCustomerPreds(newCustomerPreds)

            const message = `${res.data.results.length}件の予定売上を保存しました`
            setSnackbarMessage(message)
            setSnackbarSeverity('success')
            setSnackbarOpen(true)
        } catch (error) {
            console.error('Error bulk saving customer predictions:', error)
            setSnackbarMessage('一括保存に失敗しました')
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
        }
    }

    const handleMemoChange = (date, value) => {
        setMemos(prev => ({
            ...prev,
            [date]: value
        }))
    }

    return (
        <>
            <Typography variant="h6" margin="1rem">
                {moment(calendarYearMonth, 'YYYY-MM').format('YYYY年M月')}
            </Typography>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            {isLoading ? <CircularProgress /> :
                <SalesDataTable
                    calendarYearMonth={calendarYearMonth}
                    salePreds={salePreds}
                    mpPreds={mpPreds}
                    customerPreds={customerPreds}
                    prevYearSales={prevYearSales}
                    onCustomerPredsChange={handleCustomerPredsChange}
                    onMemoChange={handleMemoChange}
                    memos={memos}
                    onSaveCustomerPreds={handleSaveCustomerPreds}
                    onBulkSaveCustomerPreds={handleBulkSaveCustomerPreds}
                    monthlySalesPredTotal={monthlySalesPredTotal}
                    monthlyMpPredsTotal={monthlyMpPredsTotal}
                    monthlyCustomerPredsTotal={monthlyCustomerPredsTotal}
                    monthlyPrevYearSalesTotal={monthlyPrevYearSalesTotal}
                />
            }
        </>
    )
}

export default SalesAdjustments